<template>
    <div v-bind="$attrs" class="base-page-title-wrapper" :class="{ 'base-page-title-bordered': !noBorder, 'is-section': variant === 'section', 'is-step': variant === 'step' }">
        <div class="base-page-title">
            <div class="base-page-title-text">
                <h2 v-if="variant === 'step'" class="step-title">
                    <slot>{{ title }}</slot>
                </h2>
                <h3 v-else-if="variant === 'section'" class="section-title">
                    <slot>{{ title }}</slot>
                </h3>
                <BaseHeader v-else size="xl" usage="nadia" class="base-page-title-heading">
                    <slot>{{ title }}</slot>
                </BaseHeader>
                <p v-if="hasSubtitle && !subtitleBelow" class="base-page-title-subtitle" :class="{ 'whitespace-pre-line': subtitleMultiline }">
                    <slot name="subtitle">{{ subtitle }}</slot>
                </p>
            </div>
            <!-- Actions render as direct flex children (no wrapper), so a CTA that's
                 responsive-hidden (e.g. hidden md:inline-flex) leaves no phantom
                 stacking gap on mobile. Multiple actions: wrap them in one element. -->
            <slot name="actions" />
        </div>
        <!-- subtitleBelow: for pages where the text column beside the actions is too
             narrow for the sentence, the subtitle gets its own full-width row. -->
        <div v-if="hasSubtitle && subtitleBelow" class="base-page-title-subtitle" :class="{ 'whitespace-pre-line': subtitleMultiline }">
            <slot name="subtitle">{{ subtitle }}</slot>
        </div>
    </div>
</template>

<script setup lang="ts">
import BaseHeader from "~vue/components/BaseHeader.vue";
import { computed, useSlots } from "vue";

defineOptions({ inheritAttrs: false });

const props = defineProps<{
    variant?: "page" | "section" | "step";
    title: string;
    subtitle?: string;
    noBorder?: boolean;
    subtitleBelow?: boolean;
    subtitleMultiline?: boolean;
}>();

const slots = useSlots();
const hasSubtitle = computed(() => Boolean(props.subtitle || slots.subtitle));
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .base-page-title-wrapper {
        @apply flex flex-col gap-1;
    }

    .base-page-title {
        @apply flex flex-col gap-6 md:flex-row md:items-start md:justify-between md:gap-10;
    }

    .base-page-title-bordered {
        @apply border-b border-border pb-5;
    }

    .base-page-title-text {
        @apply flex grow flex-col gap-1;
    }

    .base-page-title-heading {
        @apply text-ink;
    }

    .base-page-title-subtitle {
        @apply body-small mt-1 text-page-title-subtitle;
    }

    .section-title {
        @apply title-body-bold text-ink;
    }

    /*
     * The sans section style appeared smaller than the serif content headings beneath it; use serif display
     * typography to preserve heading hierarchy.
     */
    .step-title {
        @apply display-serif text-ink;
    }
}
</style>
